<template>
  <div class="pending-input-panel">
    <AutomationRunQuestionInput
      v-for="(q, i) in questions"
      :key="i"
      :question="q"
      :workspace-id="workspaceId"
      @update:answers="(a) => onAnswerUpdate(i, a)"
    />
    <div v-if="replyError" class="reply-error">
      {{ replyError }}
    </div>
    <BaseButton
      kind="main"
      type="primary"
      text="Send"
      :state="submitting ? 'loading' : valid ? 'default' : 'disabled'"
      @click="submitReply"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch, type PropType } from "vue";

import type {
  AiAutomationRunQuestion,
  AutomationRunId,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import AutomationRunQuestionInput from "@/components/ai/AutomationRunQuestionInput.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import * as inject from "@/components/inject";

/** Whether the answers given satisfy the question's required answer count. */
function isAnswered(
  question: AiAutomationRunQuestion,
  answers: string[]
): boolean {
  switch (question.answer_count.kind) {
    case "any":
      return true;
    case "one":
      return answers.length === 1;
    case "many":
      return answers.length >= 1;
  }
}

export default defineComponent({
  name: "AutomationRunPendingInput",
  components: { AutomationRunQuestionInput, BaseButton },
  props: {
    questions: {
      type: Array as PropType<AiAutomationRunQuestion[]>,
      required: true,
    },
    organizationId: { type: String as PropType<OrgId>, required: true },
    runId: { type: String as PropType<AutomationRunId>, required: true },
    workspaceId: { type: String as PropType<WorkspaceId>, default: undefined },
  },
  setup(props) {
    const controller = inject.controller();

    const answers = ref<string[][]>(props.questions.map(() => []));

    // Reset answers when the question set changes (e.g. a different run).
    watch(
      () => props.questions,
      (qs) => {
        answers.value = qs.map(() => []);
      }
    );

    const onAnswerUpdate = (i: number, a: string[]) => {
      answers.value = answers.value.map((prev, idx) => (idx === i ? a : prev));
    };

    const valid = computed(() =>
      props.questions.every((q, i) => isAnswered(q, answers.value[i] ?? []))
    );

    const submitting = ref(false);
    const replyError = ref<string | null>(null);

    const submitReply = async () => {
      if (submitting.value || !valid.value) return;
      submitting.value = true;
      replyError.value = null;
      try {
        await controller.ai.automation.run.resume(
          props.organizationId,
          props.runId,
          answers.value
        );
      } catch (err) {
        replyError.value =
          err instanceof Error ? err.message : "Failed to send response";
      } finally {
        submitting.value = false;
      }
    };

    return {
      answers,
      onAnswerUpdate,
      valid,
      submitting,
      replyError,
      submitReply,
    };
  },
});
</script>

<style scoped lang="scss">
.pending-input-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 12px;
  border-radius: $border-radius-m;

  @include themed() {
    border: 1px solid t($grey-10);
    background-color: t($bg-alt);
  }
}

.reply-error {
  @extend %font-caption-regular;
  padding: 8px 10px;
  border-radius: $border-radius-s;

  @include themed() {
    background-color: t($secondary-10);
    color: t($secondary-80);
  }
}
</style>
